import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Settings, ChevronDown, Building2, Briefcase } from "lucide-react";
import api from "../utils/api";

const Navbar = ({ onToggleSidebar, sidebarCollapsed, activeAgentId, setActiveAgentId, agents, activeChatsCount }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  
  const userStr = sessionStorage.getItem("user");
  const user = userStr ? JSON.parse(userStr) : null;
  const isAdmin = user?.role === 'admin';
const handleLogout = () => {
  // Clear ALL stored state on sign out
  localStorage.clear();
  sessionStorage.clear();
  // Redirect to login page
  navigate('/login');
};
  const activeAgent = agents?.find((p) => String(p.id) === String(activeAgentId));
  const userTenantId = user?.tenant_id 
    ? String(user.tenant_id) 
    : (activeAgent?.tenant_id 
        ? String(activeAgent.tenant_id) 
        : (agents?.[0]?.tenant_id 
            ? String(agents[0].tenant_id) 
            : (localStorage.getItem('activeTenantId') || '')
          )
      );

  useEffect(() => {
    if (userTenantId) {
      localStorage.setItem('activeTenantId', userTenantId);
    }
  }, [userTenantId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && !event.target.closest(".agent-dropdown")) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const getPageTitle = (location) => {
    const { pathname, search } = location;

    if (pathname === "/analytics") {
      const params = new URLSearchParams(search);
      const tab = params.get("tab") || "overview";
      const tabName = tab
        .split("-")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
      return tabName;
    }

    switch (pathname) {
      case "/chat":
        return "Chat";
      case "/instructions":
        return "Agent Instructions";
      case "/models":
        return "Model Management";
      case "/api-tools":
        return "API Tools";
      case "/webhooks":
        return "Incoming Webhooks";
      case "/logo":
        return "Logo Management";
      case "/documents":
        return "Sources > Document Training";
      case "/smart-faq":
        return "Smart FAQ Builder";
      case "/widget":
        return "Widget Integration";
      case "/widget-preview":
        return "Widget Preview";
      case "/domains":
        return "Domain Management";
      case "/integrations":
        return "Integrations";
      case "/telegram-config":
        return "Integrations > Telegram";
      case "/freshchat-config":
        return "Integrations > Freshchat";
      case "/slack-config":
        return "Integrations > Slack";
      case "/email-config":
        return "Integrations > Email";
      case "/agents":
        return "Agents";
      case "/docs":
        return "Help Center";
      default:
        // try to format the path cleanly if unknown
        if (pathname === "/" || !pathname) return "Dashboard";
        return pathname
          .substring(1)
          .split("-")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");
    }
  };

  const title = getPageTitle(location);
  
  const assignedAgents = user?.assigned_agents || [];

  const visibleAgents = isAdmin
    ? agents
    : agents.filter(p => assignedAgents.includes(p.id));



  return (
    <nav className="bg-white border-b border-gray-100 px-8 py-3 flex items-center justify-between min-h-[60px] z-50">
      <div className="flex items-center gap-4">
        {typeof title === "string" && title.includes(">") ? (
          <div className="flex items-center gap-2 text-xl font-normal tracking-tight">
            {title.split(">").map((part, index, array) => (
              <React.Fragment key={index}>
                {index === 0 ? (
                  <button
                    onClick={() => navigate("/" + part.trim().toLowerCase())}
                    className="text-gray-400 hover:text-gray-900 transition-colors"
                  >
                    {part.trim()}
                  </button>
                ) : (
                  <span className="text-gray-800">{part.trim()}</span>
                )}
                {index < array.length - 1 && (
                  <span className="text-gray-300 font-light">/</span>
                )}
              </React.Fragment>
            ))}
          </div>
        ) : (
          <h1 className="text-xl font-normal text-gray-800 tracking-tight">{title}</h1>
        )}
      </div>
      <div className="flex items-center gap-6">
        
        {isAdmin || (visibleAgents && visibleAgents.length > 1) ? (
          <div className="flex items-center gap-3 agent-dropdown relative">
            <span className="text-xs text-gray-400 font-normal uppercase tracking-wider">Active Agent</span>
            <div className="relative">
              <button
                onClick={() => setIsOpen(!isOpen)}
                className={`flex items-center gap-3 px-4 py-2.5 bg-gray-50/50 border border-gray-200 rounded-xl hover:bg-white hover:shadow-md hover:border-[#83bbe1]/20 transition-all duration-300 group ${isOpen ? 'ring-2 ring-[#83bbe1]/5 border-[#83bbe1]/40 bg-white shadow-sm' : ''}`}
              >
                <div className="w-6 h-6 rounded-lg bg-gray-100 flex items-center justify-center text-gray-900 group-hover:bg-gray-200 transition-colors">
                  <Building2 size={14} />
                </div>
                <span className="text-sm font-normal text-gray-900 min-w-[120px] text-left">
                  {activeAgent?.name || "Select Agent"}
                </span>
                <ChevronDown size={16} className={`text-gray-400 transition-transform duration-300 ${isOpen ? 'rotate-180 text-gray-900' : ''}`} />
              </button>

              {isOpen && (
                <div className="absolute right-0 mt-2 w-64 bg-white border border-gray-100 rounded-2xl shadow-xl py-2 z-50 animate-in fade-in slide-in-from-top-2 duration-200">
                  <div className="px-3 py-1.5 text-[10px] font-normal text-gray-400 uppercase tracking-widest border-b border-gray-50 mb-1">
                    Your Agents
                  </div>
                  <div className="max-h-64 overflow-y-auto custom-scrollbar">
                    {visibleAgents.map((agent) => (
                      <button
                        key={agent.id}
                        onClick={() => {
                          localStorage.setItem("activeAgentId", agent.id);
                          setActiveAgentId(agent.id);
                          setIsOpen(false);
                          // Force a hard refresh to reset all component states for the new agent
                          window.location.reload();
                        }}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 transition-all hover:bg-gray-50/80 group ${String(activeAgentId) === String(agent.id) ? 'bg-gray-100' : ''}`}
                      >
                        <div className={`w-2 h-2 rounded-full transition-all ${String(activeAgentId) === String(agent.id) ? 'bg-[#83bbe1] scale-125 shadow-[0_0_8px_rgba(131,187,225,0.2)]' : 'bg-gray-200 group-hover:bg-gray-400'}`}></div>
                        <div className="flex flex-col items-start overflow-hidden">
                          <span className={`text-sm font-semibold truncate uppercase tracking-tight ${String(activeAgentId) === String(agent.id) ? 'text-[#83bbe1]' : 'text-gray-600 group-hover:text-gray-900'}`}>
                            {agent.name}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                  {isAdmin && (
                    <div className="border-t border-gray-50 mt-1 pt-1 px-1">
                      <button
                        onClick={() => {
                          navigate("/agents");
                          setIsOpen(false);
                        }}
                        className="w-full flex items-center gap-3 px-3 py-2.5 text-[10px] font-normal text-gray-900 uppercase tracking-widest hover:bg-gray-50 transition-all rounded-xl group"
                      >
                        <Settings size={12} className="text-gray-400 group-hover:text-gray-900 transition-colors" />
                        Manage All Agents
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : !isAdmin && visibleAgents && visibleAgents.length === 1 ? (
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-400 font-normal uppercase tracking-wider">Active Bot</span>
            <div className="flex items-center gap-2 px-4 py-2 bg-gray-50 border border-gray-100 rounded-xl">
              <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></div>
              <span className="text-sm font-bold text-gray-900">{visibleAgents[0].name}</span>
            </div>
          </div>
        ) : null}
      </div>
    </nav>
  );
};

export default Navbar;
